import {Component, EventEmitter, inject, OnDestroy, Output} from '@angular/core';
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {BaseService} from "../../../services/base.service";
import {LocalStorageService} from "../../../services/local-storage.service";
import {BasketImportItem, BasketImportReport, BasketImportResponse} from "../../../interfaces/basket-import";
import {HTTP_CODES} from "../../../interfaces/http-codes";
import {removeRepeatedBrand} from "../../../shared/brand-name";

@Component({
  selector: 'app-modal-upload-order',
  templateUrl: './modal-upload-order.component.html',
  styleUrls: ['./modal-upload-order.component.scss']
})
export class ModalUploadOrderComponent extends BaseService implements OnDestroy {
  @Output() importedEvent = new EventEmitter<void>();

  public isDestroyed: Subject<boolean> = new Subject();

  private http = inject(HttpClient);
  private localStorage = inject(LocalStorageService);

  isModalShow = false;
  isDarkBackground = false;
  isUploading = false;
  fileName = '';
  errorMessage = '';
  report: BasketImportReport = null;

  private selectedFile: File = null;

  // Позиции, которых на складе оказалось меньше, чем заказано
  get partialItems(): BasketImportItem[] {
    if (!this.report || !this.report.added) {
      return [];
    }

    return this.report.added.filter(item => item.added < item.requested);
  }

  openModal() {
    this.resetState();

    this.isModalShow = true;
    this.isDarkBackground = true;
  }

  closeModal() {
    this.isModalShow = false;
    this.isDarkBackground = false;

    this.resetState();
  }

  downloadTemplate() {
    window.open(this.apiRoot + 'basket_data/import_template', "_blank");
  }

  onFileSelected(event) {
    const input = event.target as HTMLInputElement;

    this.errorMessage = '';
    this.report = null;

    if (!input.files || !input.files.length) {
      this.selectedFile = null;
      this.fileName = '';
      return;
    }

    this.selectedFile = input.files[0];
    this.fileName = this.selectedFile.name;

    // Сбрасываем input, чтобы повторный выбор того же файла тоже сработал
    input.value = '';
  }

  uploadFile() {
    if (!this.selectedFile || this.isUploading) {
      return;
    }

    const delivery: string = this.localStorage.get('delivery_id');

    if (!delivery) {
      this.errorMessage = 'Не вказано спосіб доставки.';
      return;
    }

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('delivery', delivery);

    // Чистим текст прошлой неудачной попытки, иначе он останется висеть над отчетом об успешной загрузке
    this.errorMessage = '';
    this.isUploading = true;

    this.http.post<BasketImportResponse>(this.apiRoot + 'basket_data/import_file', formData)
      .pipe(takeUntil(this.isDestroyed))
      .subscribe({
        next: (response) => {
          this.isUploading = false;

          if (!response || !response.success) {
            this.errorMessage = (response && response.error) || 'Не вдалося завантажити замовлення з файлу.';
            return;
          }

          this.report = response.data;
          this.selectedFile = null;

          // Бек ставить бренд перед назвою завжди, навіть коли назва вже з нього починається (#855)
          for (const item of this.report.out_of_stock || []) {
            item.name = removeRepeatedBrand(item.name);
          }

          if (this.report.added && this.report.added.length) {
            this.importedEvent.emit();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.isUploading = false;

          // 413 віддає nginx ще до бека - файл точно не оброблено
          if (error && error.status === HTTP_CODES.PAYLOAD_TOO_LARGE) {
            this.errorMessage = 'Не вдалося завантажити замовлення з файлу.';
            return;
          }

          // Бек кладе позиції в кошик по одній і лише наприкінці віддає звіт: обрив/таймаут
          // не означає, що нічого не додано. Повторне завантаження того ж файлу задвоїло б кількості,
          // тож показуємо актуальний кошик і просимо перевірити його перед повтором.
          this.errorMessage = 'Не вдалося отримати відповідь сервера. Частина позицій могла вже потрапити до кошика — перевірте кошик перед повторним завантаженням.';
          this.importedEvent.emit();
        },
        complete: () => {
          // Завершення без next/error - запит скасовано кнопкою «Скасувати» лоадера (takeUntil в інтерцепторі).
          // Кнопка лишалась у стані «Завантаження…», а бек міг уже частково імпортувати файл - як і при обриві.
          if (this.isUploading) {
            this.isUploading = false;
            this.errorMessage = 'Завантаження скасовано. Частина позицій могла вже потрапити до кошика — перевірте кошик перед повторним завантаженням.';
            this.importedEvent.emit();
          }
        }
      });
  }

  private resetState() {
    this.isUploading = false;
    this.fileName = '';
    this.errorMessage = '';
    this.report = null;
    this.selectedFile = null;
  }

  ngOnDestroy() {
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  };
}
